/**
 * The per-row info panel — everything about a Reel Option that is true but not
 * worth a column (CAE-1552).
 *
 * `.m-popover` from the handoff, which is the kit's `Popover`: it dismisses on
 * Escape and on an outside click, and moves focus in and back to the button, so
 * the ids inside are reachable and copyable rather than merely visible.
 */
import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import { effectiveClubAbbreviation, effectiveJerseyNumber, resolveFixtureLabel } from '@s2w/core';
import type { ContestDetailReelOption, ContestFixture } from '../../../../api/contests';
import { reelOptionMarketLabel } from '../../../../contests/reel-option-market-label';
import { InfoIcon, OutcomeStatus, Popover } from '../../../../components/ui';

/**
 * What the market is taking out, read off the price.
 *
 * `null` when the catalogue no longer carries the outcome: a probability
 * derived from no price is not an estimate, it is a fabrication — §7's "no
 * stats slop" rule, the same one the Status column follows.
 */
const impliedProbability = (oddsDecimal: number | null): string =>
  oddsDecimal === null || oddsDecimal <= 0 ? '—' : `${(100 / oddsDecimal).toFixed(1)}%`;

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      padding: '7px 0',
      borderBottom: theme.tokens.border.default,
      fontSize: '12.5px',
      '&:last-of-type': { borderBottom: 0 },
    })}
  >
    <Box component="span" sx={(theme: Theme) => ({ color: theme.tokens.color.ink3 })}>
      {label}
    </Box>
    <Box
      component="span"
      sx={(theme: Theme) => ({
        color: theme.tokens.color.ink,
        fontWeight: 500,
        textAlign: 'right',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      })}
    >
      {children}
    </Box>
  </Box>
);

const Mono = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      fontFamily: theme.tokens.font.mono,
      fontVariantNumeric: 'tabular-nums',
    })}
  >
    {children}
  </Box>
);

export interface ReelOptionInfoProps {
  option: ContestDetailReelOption;
  /** The Pick Name, already resolved by the row that owns it. */
  pickName: string;
  fixture: ContestFixture | undefined;
}

export const ReelOptionInfo = ({ option, pickName, fixture }: ReelOptionInfoProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const label = `Details for ${pickName}`;

  return (
    <>
      <Tooltip title="Outcome details">
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={() => setOpen((previous) => !previous)}
          aria-label={label}
          aria-expanded={open}
          sx={(theme: Theme) => ({
            /*
             * `.rl-iconbtn`'s 28×28, held whatever glyph is inside it. Without
             * it MUI sizes `size="small"` off the glyph, and the 12px below
             * would take the hit target down to 22px — under the 24px floor,
             * on the smallest of the cell's three controls (CAE-1608).
             */
            width: '28px',
            height: '28px',
            color: open ? theme.tokens.color.accent : theme.tokens.color.ink4,
            backgroundColor: open ? theme.tokens.color.accentSoft : undefined,
          })}
        >
          {/* 12×12, the size `MIcon.info` is drawn at and the one the
              catalogue's and the drill-down's triggers set (CAE-1608). */}
          <InfoIcon sx={{ fontSize: '12px' }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        label={label}
        onClose={() => setOpen(false)}
        width={320}
      >
        <Box
          sx={(theme: Theme) => ({
            padding: '14px 16px 10px',
            borderBottom: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surfaceMuted,
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              fontSize: '10.5px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: theme.tokens.color.ink4,
            })}
          >
            {reelOptionMarketLabel(option)}
          </Box>
          <Box
            sx={(theme: Theme) => ({
              marginTop: '4px',
              fontSize: '13.5px',
              fontWeight: 600,
              letterSpacing: '-0.005em',
              color: theme.tokens.color.ink,
            })}
          >
            {pickName}
          </Box>
        </Box>

        <Box sx={{ padding: '4px 16px 12px' }}>
          <Row label="Outcome ID">
            <Mono>{option.externalOutcomeId}</Mono>
          </Row>
          <Row label="Market ID">
            <Mono>{option.externalMarketId ?? '—'}</Mono>
          </Row>
          <Row label="Fixture">{fixture ? resolveFixtureLabel(fixture) : 'Fixture not found'}</Row>
          <Row label="Fixture ID">
            <Mono>{fixture?.externalId ?? '—'}</Mono>
          </Row>
          <Row label="Decimal · Fractional">
            <Mono>
              {option.oddsDecimal === null ? '—' : option.oddsDecimal.toFixed(2)} ·{' '}
              {option.oddsFractional ?? '—'}
            </Mono>
          </Row>
          <Row label="Implied probability">
            <Mono>{impliedProbability(option.oddsDecimal)}</Mono>
          </Row>
          <Row label="Status">
            <OutcomeStatus suspended={option.suspended} />
          </Row>
          <Row label="Club · number">
            {[
              effectiveClubAbbreviation(option) ?? '—',
              effectiveJerseyNumber(option) === null ? '—' : `#${effectiveJerseyNumber(option)}`,
            ].join(' · ')}
            {option.clubAbbreviationOverride !== null || option.jerseyNumberOverride !== null
              ? ' (set by an operator)'
              : ''}
          </Row>
          <Row label="Asset">
            {option.assetId ? (
              'Uploaded'
            ) : (
              <Box
                component="em"
                sx={(theme: Theme) => ({ color: theme.tokens.color.warn, fontStyle: 'italic' })}
              >
                Missing
              </Box>
            )}
          </Row>
        </Box>
      </Popover>
    </>
  );
};
